export const useUserStore = defineStore('user', {
  state: (): UserData => ({
    id: '',
    email: '',
    emailVerified: false,
    firstName: '',
    lastName: '',
    hasSso: false,
    isQualitiesUncertain: false,
    signatureQualities: {
      ses: { any: null },
      aes: { any: null },
      aesMinimal: { eidas: null, zertes: null },
      qes: { eidas: null, zertes: null },
      seal: {
        aes: {},
        qes: {},
      },
    },
    attributes: {},
    permissions: [],
    roles: [],
    // Group data only exists for regular business users — direct-sign
    // signers are never group members, so this stays empty for them
    groupVersions: [],
  }),
  getters: {
    displayName(): string {
      if (this.firstName) return `${this.firstName} ${this.lastName}`.trimEnd()
      else return this.email
    },
    isBusinessMember(): boolean {
      const businessStore = useBusinessStore()
      return Boolean(this.id && businessStore.members.includes(this.id))
    },
    isBusinessAdmin(): boolean {
      const businessStore = useBusinessStore()
      return Boolean(this.id && businessStore.admins.includes(this.id))
    },
    isSsoSetupIncomplete(): boolean {
      const directSignStore = useDirectSignStore()
      return !directSignStore.isDirectSign && !this.firstName && !this.lastName && !this.attributes.acceptedGtc
    },
    signerIdentityEmail(): string | undefined {
      return this.attributes.signerIdentityEmail
    },
    mobile(): string | undefined {
      return this.attributes.mobile
    },
    isFreeUser(): boolean {
      return !this.isBusinessMember && !this.isBusinessAdmin
    },
    hasPermission(state) {
      return (permission: PermissionAtom) => state.permissions.includes(permission)
    },
    canCreateSignatureRequest(): boolean {
      return this.hasPermission('sr:create')
    },
    highestSignatureQuality(state): string | null {
      if (!state.emailVerified) return null

      if (state.signatureQualities?.qes?.eidas || state.signatureQualities?.qes?.zertes) {
        // If a user did an ident they can only sign with eIDAS. However,
        // it is possible to end up in a ZertES-only case when the user only
        // accepted the ZertES GTC (and not the eIDAS GTC) of Swisscom.
        // Therefore we need to check both keys for 'demo'.
        if (state.signatureQualities.qes.eidas?.quality?.toLowerCase() === 'demo') {
          return 'demo'
        } else if (state.signatureQualities.qes.zertes?.quality?.toLowerCase() === 'demo') {
          return 'demo'
        } else {
          return 'qes'
        }
      } else if (state.signatureQualities?.aes?.any) {
        return 'aes'
      } else if (state.signatureQualities?.ses?.any) {
        return 'ses'
      }

      return null
    },
    part11SignerData(): { name: string; reason: string } | null {
      const name = this.attributes.p11SigningName
      const reason = this.attributes.p11SigningReason

      if (!name || !reason) return null

      return { name, reason }
    },
    preferredLanguage(): Language {
      return this.attributes.lang ?? 'en'
    },
    hasImmutableMobile(): boolean {
      return this.attributes.immutableEidFields?.includes('mobile') || false
    },
    appliedReferralCode(): string | undefined {
      return this.attributes.celloUcc
    },
    role(): AppRole {
      if (!this.id) return 'unauthenticated'

      const directSignStore = useDirectSignStore()

      if (directSignStore.isDirectSign) return 'nas'

      if (this.isBusinessAdmin) return 'admin'
      if (this.isBusinessMember) return 'member'

      return 'user'
    },
    /**
     * Returns the inherited roles of the user.
     */
    accessRoles(): Set<AppRole> {
      const accessRoles = new Set<AppRole>([this.role])

      if (this.role === 'admin') {
        accessRoles.add('member')
        accessRoles.add('user')
      } else if (this.role === 'member') {
        accessRoles.add('user')
      }

      return accessRoles
    },
  },
  actions: {
    async registerUser(email: string, registerData: RegisterUserPayload) {
      const { userRepository } = useLegacyApi()

      await userRepository.register(registerData)
    },
    ensureUser(strategy: EnsureStrategy = 'cache-first') {
      return this.ensureOnce('user', () => this.fetchUser(), strategy)
    },
    async fetchUser() {
      const { $api } = useNuxtApp()

      const directSignStore = useDirectSignStore()
      const languageStore = useLanguageStore()

      const { directUserRepository } = useLegacyApi()
      const { loadSignatureQualities } = useSignatureQualities()

      // Fetch user data, signature qualities and group membership in parallel.
      // The direct-sign response still carries the legacy visual-signature
      // `permissions` object, which must not land in the store's capability set
      const [{ attributes, ...userData }, { signatureQualities, isQualitiesUncertain }, groups] = await Promise.all([
        directSignStore.isDirectSign
          ? directUserRepository.get().then(({ permissions: _legacyPermissions, ...user }) => user)
          : $api('/api/users/me').then(({ roles, ...user }) => ({ ...user, roles: roles ?? [] })),
        loadSignatureQualities(this.signatureQualities),
        // Direct-sign signers are never group members, so we skip it
        directSignStore.isDirectSign ? Promise.resolve(null) : $api('/api/users/me/groups'),
      ])

      const { firstName, lastName, email, emailVerified, ...userAttributes } = attributes

      const userLanguage = userAttributes.lang

      const userIdentity = `${firstName ?? ''} ${lastName ?? ''}`.trim() || email

      if (directSignStore.isDirectSign) {
        const visualSignatureStore = useVisualSignatureStore()

        const defaultSignature = {
          name: userIdentity ?? '',
          language: userLanguage ?? 'en',
          optionalLine: '',
          optionalLine2: '',
          displayClaim: false,
          displayStandards: false,
          displayQr: false,
          displayDate: true,
        }

        visualSignatureStore.$patch({
          signatures: [defaultSignature],
        })
      }

      this.$patch({
        ...userData,
        signatureQualities,
        firstName: String(firstName ?? ''),
        lastName: String(lastName ?? ''),
        email: email ?? '',
        emailVerified: emailVerified ?? false,
        groupVersions: groups?.groupVersions ?? [],
      })

      // We cannot rely on a stable response structure from the backend,
      // so we need to ensure that potentially disappeared attributes
      // are unset (full replacement vs. patch)
      this.attributes = userAttributes

      this.isQualitiesUncertain = isQualitiesUncertain

      // Language for NAS signers is set via middleware 05.i18n.global.ts,
      // so we skip changing it here
      if (userLanguage && !directSignStore.isDirectSign) {
        await languageStore.setLanguage(userLanguage)
      }
    },
    async fetchUserBusiness() {
      const { $api } = useNuxtApp()

      if (!this.id) return null

      return await $api('/api/users/me/business')
    },
    async loadUserBusinessData() {
      const business = await this.fetchUserBusiness()
      if (!business) return
      const stripeSubscriptionStatus = business.billing?.stripeSubscriptionStatus
      if (stripeSubscriptionStatus) {
        Object.assign(business, { stripe: { subscriptionStatus: stripeSubscriptionStatus } })
      }

      const businessStore = useBusinessStore()
      businessStore.$patch(business)
    },
    async setPreferredLanguage(language: Language) {
      if (!this.id) return

      const { updateAttributes } = useUserAttributes()
      await updateAttributes({ lang: language })
    },
    async setNotificationLevel(level: EmailNotificationLevel) {
      if (!this.id) return

      const { updateAttributes } = useUserAttributes()
      await updateAttributes({ emailNotificationLevel: level })
    },
    async setDefaultLegislation(legislation: string) {
      if (!this.id) return

      const { updateAttributes } = useUserAttributes()
      await updateAttributes({ defaultLegislation: legislation })
    },
    async setDefaultQuality(quality: string) {
      if (!this.id) return

      const { updateAttributes } = useUserAttributes()
      await updateAttributes({ defaultQuality: quality })
    },
    clearUser() {
      const authStore = useAuthStore()
      const businessStore = useBusinessStore()
      this.$clearEnsured()
      this.$reset()
      businessStore.clearBusiness()
      authStore.removeStoredToken()
    },
    logoutUser() {
      const { $sentry, $auth } = useNuxtApp()
      const { apisix } = useRuntimeConfig().public
      const { resetIdentity } = useAnalytics()

      this.clearUser()

      $sentry?.setUser(null)
      resetIdentity()
      $auth.signalLogout()
      window.location.href = `${apisix.url}/logout`
    },
    async accept(terms: 'gtc' | 'tou' | 'biz') {
      const { userRepository } = useLegacyApi()

      switch (terms) {
        case 'gtc':
          return await userRepository.acceptGeneralTerms()
        case 'tou':
          return await userRepository.acceptSwisscomTerms()
        case 'biz':
          return await userRepository.acceptBusinessInvitation()
      }
    },
  },
})
